import { AnimatePresence, motion } from "motion/react";
import { Field, NumberInput, TextInput } from "../../components/fields";
import { Picker } from "../../components/Picker";
import { Button, IconButton } from "../../components/ui";
import { fieldLabels } from "../../i18n/fieldLabels";
import {
  clarityLabels,
  currencyLabels,
  cutLabels,
  optionsOf,
  stoneShapeLabels,
  stoneTypeLabels,
} from "../../i18n/choices";
import { formatToman } from "../../i18n/format";
import { pick } from "../../i18n/labels";
import { useT } from "../../i18n/strings";
import { micro } from "../../motion";
import { rialPerUnit } from "../prices/quote";
import type {
  Clarity,
  Currency,
  CutGrade,
  PriceBoard,
  StoneShape,
  StoneType,
} from "../../api/types";
import { stoneError, type FormErrors, type StoneDraft } from "./usePieceForm";
import s from "./form.module.css";

interface StonesEditorProps {
  stones: StoneDraft[];
  errors: FormErrors;
  board: PriceBoard | undefined;
  onChange: (index: number, changes: Partial<StoneDraft>) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

/** One card per line of stones: type and count first, the 4Cs after. */
export function StonesEditor({
  stones,
  errors,
  board,
  onChange,
  onAdd,
  onRemove,
}: StonesEditorProps) {
  const t = useT();

  /** What the line comes to in toman, so a dollar price is never taken on faith. */
  const lineHint = (stone: StoneDraft): string | undefined => {
    if (stone.price === null || stone.currency === "IRT") return undefined;
    const perUnit = rialPerUnit(stone.currency, board);
    if (perUnit === null) return board ? t.form.stoneNoRate : undefined;
    return t.form.stoneLineToman(formatToman(stone.price * (stone.count ?? 1) * perUnit));
  };

  return (
    <div className={s.stones}>
      {stones.length === 0 && <p className={s.note}>{t.form.stonesEmpty}</p>}

      <AnimatePresence initial={false}>
        {stones.map((stone, index) => (
          <motion.div
            key={stone.key}
            className={s.stoneLine}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={micro}
          >
            <div className={s.stoneHead}>
              <span>{t.form.stoneLine(index + 1)}</span>
              <IconButton name="delete" label={t.actions.remove} onClick={() => onRemove(index)} />
            </div>

            <Field label={fieldLabels.stone_type} error={stoneError(errors, index, "stone_type")}>
              <Picker
                title={pick(fieldLabels.stone_type)}
                options={optionsOf(stoneTypeLabels)}
                value={stone.stone_type || null}
                clearable={false}
                onChange={(id) => onChange(index, { stone_type: (id ?? "") as StoneType })}
              />
            </Field>

            <div className={s.yearRow}>
              <Field label={fieldLabels.count} error={stoneError(errors, index, "count")}>
                <NumberInput
                  step="1"
                  min={1}
                  value={stone.count}
                  invalid={Boolean(stoneError(errors, index, "count"))}
                  onChange={(value) => onChange(index, { count: value })}
                />
              </Field>
              <Field
                label={{ fa: t.form.caratTotal, en: t.form.caratTotal }}
                optional
                error={stoneError(errors, index, "carat")}
              >
                <NumberInput
                  min={0}
                  value={stone.carat}
                  invalid={Boolean(stoneError(errors, index, "carat"))}
                  onChange={(value) => onChange(index, { carat: value })}
                />
              </Field>
            </div>

            <div className={s.yearRow}>
              <Field
                label={{ fa: t.form.stonePrice, en: t.form.stonePrice }}
                error={stoneError(errors, index, "price")}
                hint={lineHint(stone)}
              >
                <NumberInput
                  min={0}
                  value={stone.price}
                  invalid={Boolean(stoneError(errors, index, "price"))}
                  onChange={(value) => onChange(index, { price: value })}
                />
              </Field>
              <Field label={fieldLabels.currency}>
                <Picker
                  title={pick(fieldLabels.currency)}
                  options={optionsOf(currencyLabels)}
                  value={stone.currency}
                  clearable={false}
                  onChange={(id) => id && onChange(index, { currency: id as Currency })}
                />
              </Field>
            </div>

            <Field label={fieldLabels.shape} optional>
              <Picker
                title={pick(fieldLabels.shape)}
                options={optionsOf(stoneShapeLabels)}
                value={stone.shape || null}
                placeholder={t.form.none}
                onChange={(id) => onChange(index, { shape: (id ?? "") as StoneShape | "" })}
              />
            </Field>

            <div className={s.yearRow}>
              <Field label={fieldLabels.clarity} optional>
                <Picker
                  title={pick(fieldLabels.clarity)}
                  options={optionsOf(clarityLabels)}
                  value={stone.clarity || null}
                  placeholder={t.form.none}
                  onChange={(id) => onChange(index, { clarity: (id ?? "") as Clarity | "" })}
                />
              </Field>
              <Field label={fieldLabels.cut} optional>
                <Picker
                  title={pick(fieldLabels.cut)}
                  options={optionsOf(cutLabels)}
                  value={stone.cut || null}
                  placeholder={t.form.none}
                  onChange={(id) => onChange(index, { cut: (id ?? "") as CutGrade | "" })}
                />
              </Field>
            </div>

            <Field label={fieldLabels.color} optional>
              <TextInput
                auto
                value={stone.color}
                placeholder={t.form.colorPlaceholder}
                onChange={(value) => onChange(index, { color: value })}
              />
            </Field>

            <Field label={fieldLabels.certificate_ref} optional>
              <TextInput
                value={stone.certificate_ref}
                placeholder={t.form.certificatePlaceholder}
                onChange={(value) => onChange(index, { certificate_ref: value })}
              />
            </Field>
          </motion.div>
        ))}
      </AnimatePresence>

      <Button variant="secondary" icon="add" onClick={onAdd}>
        {t.form.addStone}
      </Button>
    </div>
  );
}
